import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { makeStyles, useTheme, withStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';
import DOMPurify from 'dompurify';

import Link from '@material-ui/core/Link';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Collapse from '@material-ui/core/Collapse';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Divider from '@material-ui/core/Divider';

import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import MoreVertIcon from '@material-ui/icons/MoreVert';

import CommentContainer from 'app/components/comments/CommentContainer';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    feedItem: {
        marginTop: theme.spacing(3)
    },
    marginLeft: {
        marginLeft: theme.spacing(1)
    },
    groupLink: {
        verticalAlign: 'inherit'
    },
    cardContentCollapsed: {
        paddingTop: 0,
        position: 'relative'
    },
    message: {
        wordBreak: 'break-word'
    }
}));

export default function FeedPostItem(props) {

    const { app } = useApp();
    const api = useApi();
    const theme = useTheme();

    const classes = useStyles();

    const { post, onEdit, onDelete } = props;

    const [ commentsExpanded, setCommentsExpanded ] = useState(false);
    const [ commentsCount, setCommentsCount ] = useState(0);

    const [ vote, setVote ] = useState(null);
    const [ upvotes, setUpvotes ] = useState(0);
    const [ downvotes, setDownvotes ] = useState(0);

    const [ settingsAnchorEl, setSettingsAnchorEl] = useState(null);

    useEffect(() => {
        if (post.votes.length) {
            setVote(Boolean(post.votes[0].votes.vote))
        }

        setUpvotes(parseInt(post.upvotes_count, 10));
        setDownvotes(parseInt(post.downvotes_count, 10));
        setCommentsCount(parseInt(post.comments_count, 10))
    }, [post]);

    const handleSettingsOpen = e => {
        setSettingsAnchorEl(e.currentTarget);
    };

    const handleSettingsClose = () => {
        setSettingsAnchorEl(null);
    };

    const increaseCommentsCount = () => {
        console.log(commentsCount + 1);
        setCommentsCount(commentsCount + 1);
    }

    const decreaseCommentsCount = () => {
        setCommentsCount(commentsCount - 1);
    }

    const postSettingsId = useMemo(() => {
        return "feed-" + post.type + "-settings-" + post.id;
    }, [post]);

    const toggleComments = () => {
        setCommentsExpanded(!commentsExpanded);
    }

    const commentButtonText = useMemo(() => {
        let s = '';

        if (commentsCount != 1) s = 's';

        return `${commentsCount} Comment${s}`;
    }, [commentsCount]);

    const postMessage = useMemo(() => {
        if (!post.message) return "";

        var urlRegex = /(((https?:\/\/)|(www\.))[^\s]+)/g;

        return DOMPurify.sanitize(post.message).replace(urlRegex, function (url) {
          var hyperlink = url;

          if (!hyperlink.match('^https?:\/\/')) {
            hyperlink = 'http://' + hyperlink;
          }

          return '<a href="' + hyperlink + '" target="_blank" rel="noopener noreferrer">' + url + '</a>';
        });
    }, [post]);

    const postUpvote = () => {
        if (vote === true) {
            setVote(null);
            setUpvotes(upvotes => upvotes - 1);
            api.get('/post/' + post.id + '/novote');
        } else {
            if (vote === false) setDownvotes(downvotes => downvotes - 1)
            setVote(true);
            setUpvotes(upvotes => upvotes + 1);
            api.get('/post/' + post.id + '/upvote');
        }
    }

    const postDownvote = () => {
        if (vote === false) {
            setVote(null);
            setDownvotes(downvotes => downvotes - 1);
            api.get('/post/' + post.id + '/novote');
        } else {
            if (vote === true) setUpvotes(upvotes => upvotes - 1)
            setVote(false);
            setDownvotes(downvotes => downvotes + 1);
            api.get('/post/' + post.id + '/downvote');
        }
    }

    const subHeader = (
        <div>
            <Typography variant='body2' color="textSecondary" display='inline'>
                Posted to&nbsp;
                {(post.group_id && post.group_id > 0) ? (
                    <Tooltip title={"Viewable to " + post.group.title} aria-label={"Viewable to " + post.group.title}>
                        <Link
                            variant="subtitle2"
                            component="button"
                            className={classes.groupLink}
                            color="secondary"
                            >
                            {post.group.title}
                        </Link>
                    </Tooltip>
                ) : (
                    <Tooltip title="Viewable to everyone" aria-label="Viewable to everyone">
                        <Link
                            variant="subtitle2"
                            component="button"
                            className={classes.groupLink}
                            color="secondary"
                            >
                            {app.club.name}
                        </Link>
                    </Tooltip>
                )}
                &nbsp;{DateTime.fromISO(post.created_at).toRelative()}
            </Typography>
        </div>
    )

    const StyledAvatar = withStyles({
        root: {
            width: 27,
            height: 27,
            fontSize: 14
        },
        colorDefault: {
            backgroundColor: app.club.branding.primary_color,
            color: theme.palette.getContrastText(app.club.branding.primary_color),
        }
    })(Avatar);

    return (
        <Card className={classes.feedItem}>
            {post.user_id === 0 ? (
                <CardHeader
                    avatar={
                        <StyledAvatar
                            src={app.club.branding.icon ? app.club.branding.icon.full_url : null}
                            alt={app.club.name}
                            variant="rounded"
                            >
                            {app.club.branding.acronym_short}
                        </StyledAvatar>
                    }
                    title={
                        <Typography variant='subtitle2' color="textPrimary">
                            {app.club.name}
                        </Typography>
                    }
                    subheader={subHeader}
                />
            ) : (
                <CardHeader
                    avatar={
                        <Avatar
                            aria-label={post.user.full_name}
                            alt={post.user.full_name}
                            className={classes.avatar}
                            src={post.user.avatar ? post.user.avatar.full_url : null}
                            >
                            {post.user.initials}
                        </Avatar>
                    }
                    action={
                        <IconButton onClick={handleSettingsOpen} aria-controls={postSettingsId} aria-haspopup="true">
                            <MoreVertIcon />
                        </IconButton>
                    }
                    title={
                        <div>
                            <Typography variant='subtitle2' color="textPrimary" display='inline'>
                                {post.user.full_name}
                            </Typography>
                            {(post.created_at != post.updated_at) &&
                                <Typography className={classes.marginLeft} variant='body2' display='inline' color="textSecondary">
                                    Edited
                                </Typography>
                            }
                        </div>
                    }
                    subheader={subHeader}
                />
            )}

            <CardContent>
                <Typography className={classes.message} variant="body1" component="p" dangerouslySetInnerHTML={{ __html: postMessage }} />
            </CardContent>
            {(post.voteable || post.commentable) &&
                <CardActions disableSpacing>
                    <Grid container justify="space-between" alignItems="center">
                        <Grid item>
                            {post.voteable &&
                                <Fragment>
                                    <Button
                                        startIcon={<ThumbUpIcon/>}
                                        aria-label="Upvote"
                                        onClick={postUpvote}
                                        color={(vote === true) ? 'secondary' : 'default'}
                                        >
                                        {upvotes}
                                    </Button>
                                    <Button
                                        startIcon={<ThumbDownIcon/>}
                                        aria-label="Downvote"
                                        onClick={postDownvote}
                                        color={(vote === false) ? 'secondary' : 'default'}
                                        >
                                        {downvotes}
                                    </Button>
                                </Fragment>
                            }
                        </Grid>
                        <Grid item>
                            {post.commentable &&
                                <Button onClick={toggleComments} aria-label="View / Add Comments">
                                    <span>{commentButtonText}</span>
                                </Button>
                            }
                        </Grid>
                    </Grid>
                </CardActions>
            }
            {post.commentable &&
                <Collapse in={commentsExpanded} timeout="auto">
                    <Divider />
                    <CommentContainer
                        commentableType="posts"
                        commentableId={post.id}
                        count={commentsCount}
                        onPostComment={increaseCommentsCount}
                        onDeleteComment={decreaseCommentsCount}
                    />
                </Collapse>
            }
            <Menu
                id={postSettingsId}
                anchorEl={settingsAnchorEl}
                keepMounted
                open={Boolean(settingsAnchorEl)}
                onClose={handleSettingsClose}
                >
                {(post.type == 'post' && post.user_id == app.user.id) &&
                    <MenuItem onClick={onEdit}>Edit Post</MenuItem>
                }
                {(post.type == 'post' && post.user_id == app.user.id) &&
                    <MenuItem onClick={onDelete}>Delete Post</MenuItem>
                }
                <MenuItem>Tag as Inappropriate</MenuItem>
            </Menu>
        </Card>
    )
}
